import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useMasterData } from "../../../hooks/useMasterData";
import { bplStudentsApi, BplStudentFilters } from "./bplStudentsApi";
import { Student, StudentStatus } from "../students/api";

const BPLStudentsList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { masters, fetchMasters, fetchSections: fetchSectionsHook } =
    useMasterData();

  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [filters, setFilters] = useState<BplStudentFilters>({
    sessionId: "",
    classId: "",
    sectionId: "",
    status: "",
    search: "",
  });

  const [sections, setSections] = useState<any[]>([]);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filters]);

  useEffect(() => {
    if (filters.classId) {
      (async () => {
        const secs = await fetchSectionsHook(filters.classId as string);
        setSections(secs);
      })();
    } else {
      setSections([]);
    }
  }, [filters.classId, fetchSectionsHook]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await bplStudentsApi.getAll({
        page: page + 1,
        limit,
        filters,
      });
      setStudents(response.data);
      setTotal(response.meta.total);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof BplStudentFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = async (format: "csv" | "excel") => {
    if (!hasPermission("BPL_STUDENT_EXPORT")) {
      return;
    }
    if (total === 0) {
      return;
    }
    setExporting(true);
    try {
      const response = await bplStudentsApi.getAll({
        page: 1,
        limit: total,
        filters,
      });
      const rows: Student[] = response.data || [];

      const header = [
        "Admission No",
        "Scholar No",
        "Student Name",
        "Father Name",
        "Class",
        "Section",
        "Admission Date",
        "Session",
      ];

      const dataRows = rows.map((s) => {
        const admissionNumber = s.admissionNumber || "";
        const scholarNumber = s.scholarNumber || "";
        const name = `${s.firstName} ${s.lastName || ""}`.trim();
        const fatherName = s.parent?.fatherName || "";
        const className = s.class?.name || "";
        const sectionName = s.section?.name || "";
        const admissionDate = s.admissionDate
          ? new Date(s.admissionDate).toLocaleDateString()
          : "";
        const sessionName =
          (s as any).academicRecords?.[0]?.academicSession?.name || "";

        return [
          admissionNumber,
          scholarNumber,
          name,
          fatherName,
          className,
          sectionName,
          admissionDate,
          sessionName,
        ];
      });

      if (format === "csv") {
        const lines = [header.join(",")];
        dataRows.forEach((row) => {
          const line = row
            .map((value) => {
              const v = value || "";
              if (v.includes(",") || v.includes('"')) {
                return `"${v.replace(/"/g, '""')}"`;
              }
              return v;
            })
            .join(",");
          lines.push(line);
        });

        const csvContent = lines.join("\r\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "bpl-students.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        const rowsForExcel = [header, ...dataRows];
        const csvContent = rowsForExcel
          .map((row) =>
            row
              .map((value) => {
                const v = value || "";
                if (v.includes(",") || v.includes('"')) {
                  return `"${v.replace(/"/g, '""')}"`;
                }
                return v;
              })
              .join(","),
          )
          .join("\r\n");

        const blob = new Blob([csvContent], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "bpl-students.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch {
    } finally {
      setExporting(false);
    }
  };

  const stats = useMemo(() => {
    const totalStudents = total;
    const active = students.filter(
      (s) => s.status === StudentStatus.ACTIVE,
    ).length;
    const now = new Date();
    const currentYear = now.getFullYear();
    const newAdmissions = students.filter((s) => {
      if (!s.admissionDate) return false;
      const d = new Date(s.admissionDate);
      return d.getFullYear() === currentYear;
    }).length;

    const classCounts: Record<string, number> = {};
    students.forEach((s) => {
      const key = s.class?.name || "Unknown";
      classCounts[key] = (classCounts[key] || 0) + 1;
    });

    return {
      totalStudents,
      active,
      newAdmissions,
      classCounts,
    };
  }, [students, total]);

  const classChartData = useMemo(() => {
    return Object.keys(stats.classCounts).map((className) => ({
      className,
      students: stats.classCounts[className],
    }));
  }, [stats.classCounts]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case StudentStatus.ACTIVE:
        return "success";
      case StudentStatus.INACTIVE:
        return "error";
      case StudentStatus.PASSED_OUT:
        return "info";
      case StudentStatus.TC:
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          BPL Students
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Monitor and report students marked as BPL (Below Poverty Line)
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Total BPL Students
            </Typography>
            <Typography variant="h5">{stats.totalStudents}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Active BPL Students
            </Typography>
            <Typography variant="h5">{stats.active}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              New Admissions This Year
            </Typography>
            <Typography variant="h5">{stats.newAdmissions}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Class-wise BPL Count
            </Typography>
            <Typography variant="h5">
              {Object.keys(stats.classCounts).length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6">BPL Student List</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => handleExport("csv")}
            disabled={exporting || loading || total === 0}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => handleExport("excel")}
            disabled={exporting || loading || total === 0}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Name, Adm No, Scholar No..."
              value={filters.search || ""}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Session"
              value={filters.sessionId || ""}
              onChange={(e) => handleFilterChange("sessionId", e.target.value)}
            >
              <MenuItem value="">All Sessions</MenuItem>
              {(masters.academicSessions || []).map((s: any) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Class"
              value={filters.classId || ""}
              onChange={(e) => handleFilterChange("classId", e.target.value)}
            >
              <MenuItem value="">All Classes</MenuItem>
              {(masters.classes || []).map((cls: any) => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Section"
              value={filters.sectionId || ""}
              onChange={(e) => handleFilterChange("sectionId", e.target.value)}
              disabled={!filters.classId}
            >
              <MenuItem value="">All Sections</MenuItem>
              {sections.map((sec: any) => (
                <MenuItem key={sec.id} value={sec.id}>
                  {sec.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.status || ""}
              onChange={(e) =>
                handleFilterChange("status", e.target.value as string)
              }
            >
              <MenuItem value="">All Status</MenuItem>
              {Object.values(StudentStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {status.replace("_", " ")}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Admission No</TableCell>
                    <TableCell>Scholar No</TableCell>
                    <TableCell>Student Name</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Section</TableCell>
                    <TableCell>Admission Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : students.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No BPL students found
                      </TableCell>
                    </TableRow>
                  ) : (
                    students.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell>{student.admissionNumber}</TableCell>
                        <TableCell>{student.scholarNumber}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {student.firstName} {student.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>{student.class?.name}</TableCell>
                        <TableCell>{student.section?.name}</TableCell>
                        <TableCell>
                          {student.admissionDate
                            ? new Date(student.admissionDate).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.status}
                            size="small"
                            color={getStatusColor(student.status)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Student Profile">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                navigate(
                                  `/scholar-register/bpl-students/${student.id}`,
                                )
                              }
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Open Full Profile">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/scholar-register/students/${student.id}`,
                                )
                              }
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={handleLimitChange}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" gutterBottom>
              Class-wise BPL Distribution
            </Typography>
            <Box sx={{ height: 260 }}>
              {classChartData.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No data available.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={classChartData}>
                    <XAxis dataKey="className" />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip />
                    <Bar dataKey="students" fill="#66bb6a" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BPLStudentsList;
